import styled from '@emotion/styled';
import { ComponentProps, useLayoutEffect, useMemo, useState } from 'react';
import { usePreviousDistinct } from 'react-use';
import { GraphicSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import { VideoProvider } from '~/interfaces';

interface Props extends ComponentProps<typeof Image> {
  videoId: string;
  provider?: VideoProvider;
}

// Thumbs por provider
function buildThumbUrl(provider: VideoProvider | undefined, videoId: string) {
  const p = provider ?? 'youtube';

  switch (p) {
    case 'youtube':
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

    case 'dailymotion':
      // Dailymotion da thumb por endpoint (sin API key)
      // Nota: a veces redirige, pero funciona bien en <img>
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;

    case 'vimeo':
      // Vimeo NO tiene thumbnail directo sin API/oEmbed.
      // Fallback: ponemos negro (o podrías usar una imagen local).
      return '';
  }
}

function Tile({ videoId, provider, ...props }: Props) {
  const src = buildThumbUrl(provider, videoId);

  // Si no hay src (Vimeo), mostramos un “placeholder”
  if (!src) {
    return <Image src="/karaoke/placeholder.jpg" alt="Video thumbnail" {...props} />;
  }

  return <Image src={src} alt={`Thumbnail for ${provider ?? 'youtube'} video ${videoId}`} {...props} />;
}

function BackgroundThumbnail({ videoId, provider, ...props }: Props) {
  const previousVideoId = usePreviousDistinct(videoId) ?? videoId;
  const previousProvider = usePreviousDistinct(provider) ?? provider;

  const [visible, setVisible] = useState(true);
  useLayoutEffect(() => {
    setVisible((current) => !current);
  }, [videoId, provider]);

  const [graphicSettings] = useSettingValue(GraphicSetting);
  if (graphicSettings === 'low') return null;

  const front = useMemo(
    () => ({ videoId: visible ? videoId : previousVideoId, provider: visible ? provider : previousProvider }),
    [visible, videoId, previousVideoId, provider, previousProvider],
  );
  const back = useMemo(
    () => ({ videoId: visible ? previousVideoId : videoId, provider: visible ? previousProvider : provider }),
    [visible, videoId, previousVideoId, provider, previousProvider],
  );

  return (
    <Container {...props}>
      <TileAnimated videoId={front.videoId} provider={front.provider} visible={visible} />
      <TileAnimated videoId={back.videoId} provider={back.provider} visible={!visible} />
      <BaseTile videoId={previousVideoId} provider={previousProvider} />
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  overflow: hidden;
  background: black;
`;

const BaseTile = styled(Tile)`
  visibility: hidden;
`;

const TileAnimated = styled(Tile) <{ visible: boolean }>`
  position: absolute;
  transition: opacity 300ms;
  opacity: ${(props) => (props.visible ? 1 : 0)};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export default BackgroundThumbnail;